import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Camera, ArrowsCounterClockwise, Upload, Check, X } from "@phosphor-icons/react";

/**
 * Modal que captura una selfie del usuario para usar como textura facial en el avatar 3D.
 * Flujo: webcam preview EN VIVO -> snapshot -> canvas crop cuadrado 512x512 -> base64 -> onSelected.
 * Alternativa: subir archivo de imagen.
 */
export default function SelfieCapture({ open, onClose, onSelected, currentPhoto }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const fileRef = useRef(null);
  const [mode, setMode] = useState("webcam"); // webcam | upload
  const [snapshot, setSnapshot] = useState(currentPhoto || "");
  const [facingMode, setFacingMode] = useState("user"); // user | environment
  const [starting, setStarting] = useState(false);

  // Start / stop webcam stream based on open + mode
  useEffect(() => {
    if (!open || mode !== "webcam") {
      stopStream();
      return;
    }
    if (snapshot) return; // already captured, don't reopen webcam
    startStream();
    return () => stopStream();
    // eslint-disable-next-line
  }, [open, mode, facingMode]);

  // Reset when closing
  useEffect(() => {
    if (!open) {
      setSnapshot(currentPhoto || "");
    }
  }, [open, currentPhoto]);

  const startStream = async () => {
    setStarting(true);
    try {
      stopStream();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode, width: { ideal: 720 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
    } catch (e) {
      toast.error(e?.message || "No se pudo acceder a la cámara");
      setMode("upload");
    } finally {
      setStarting(false);
    }
  };

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const takeSnapshot = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    const size = 512;
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    // Center-crop square
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    const min = Math.min(vw, vh);
    const sx = (vw - min) / 2;
    const sy = (vh - min) / 2;
    // Mirror selfie for user-facing camera
    if (facingMode === "user") {
      ctx.translate(size, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(video, sx, sy, min, min, 0, 0, size, size);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
    setSnapshot(dataUrl);
    stopStream();
  };

  const retake = () => {
    setSnapshot("");
    if (mode === "webcam") startStream();
  };

  const handleUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5_000_000) { toast.error("Archivo muy grande (max 5MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => {
      // Resize to 512x512 square
      const img = new Image();
      img.onload = () => {
        const canvas = canvasRef.current || document.createElement("canvas");
        const size = 512;
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d");
        const min = Math.min(img.width, img.height);
        const sx = (img.width - min) / 2;
        const sy = (img.height - min) / 2;
        ctx.drawImage(img, sx, sy, min, min, 0, 0, size, size);
        setSnapshot(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(f);
  };

  const apply = () => {
    if (!snapshot) { toast.error("Captura o sube una foto"); return; }
    onSelected(snapshot);
    stopStream();
    onClose();
  };

  const remove = () => {
    onSelected("");
    setSnapshot("");
    stopStream();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { stopStream(); onClose(); } }}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-md" data-testid="selfie-modal">
        <DialogHeader>
          <DialogTitle className="font-['Barlow_Condensed'] uppercase text-xl flex items-center gap-2">
            <Camera size={20} className="text-[#CCFF00]" /> Personaliza tu rostro
          </DialogTitle>
        </DialogHeader>

        {/* Mode toggle */}
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => { setMode("webcam"); setSnapshot(""); }} className={`text-[10px] uppercase font-bold ${mode === "webcam" ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "border-white/15"}`} data-testid="selfie-mode-webcam">
            <Camera size={12} className="mr-1" /> Webcam
          </Button>
          <Button size="sm" variant="outline" onClick={() => { setMode("upload"); stopStream(); }} className={`text-[10px] uppercase font-bold ${mode === "upload" ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "border-white/15"}`} data-testid="selfie-mode-upload">
            <Upload size={12} className="mr-1" /> Subir foto
          </Button>
        </div>

        {/* Preview area */}
        <div className="relative aspect-square bg-[#070707] border border-white/10 rounded overflow-hidden">
          {snapshot ? (
            <img src={snapshot} alt="Selfie" className="w-full h-full object-cover" data-testid="selfie-preview" />
          ) : mode === "webcam" ? (
            <>
              <video ref={videoRef} className={`w-full h-full object-cover ${facingMode === "user" ? "scale-x-[-1]" : ""}`} playsInline muted autoPlay data-testid="selfie-video" />
              {starting && <div className="absolute inset-0 flex items-center justify-center text-[10px] text-gray-400 uppercase tracking-wide">Iniciando cámara...</div>}
              {/* Center face guide */}
              <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                <div className="w-3/4 h-3/4 border-2 border-dashed border-[#CCFF00]/40 rounded-full" />
              </div>
            </>
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
              <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" data-testid="selfie-file-input" />
              <Button onClick={() => fileRef.current?.click()} variant="outline" className="border-[#CCFF00]/40 text-[#CCFF00]">
                <Upload size={14} className="mr-1" /> Seleccionar archivo
              </Button>
              <span className="text-[10px] text-gray-500">JPG/PNG · max 5MB</span>
            </div>
          )}
        </div>
        <canvas ref={canvasRef} className="hidden" />

        {/* Action row */}
        {mode === "webcam" && !snapshot && (
          <div className="flex gap-2">
            <Button onClick={() => setFacingMode((m) => m === "user" ? "environment" : "user")} variant="outline" size="sm" className="border-white/15 text-[10px] uppercase">
              <ArrowsCounterClockwise size={12} className="mr-1" /> Girar
            </Button>
            <Button onClick={takeSnapshot} className="flex-1 bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase" data-testid="selfie-snap">
              <Camera size={14} className="mr-1" /> Capturar
            </Button>
          </div>
        )}
        {snapshot && (
          <div className="flex gap-2">
            <Button onClick={retake} variant="outline" size="sm" className="border-white/15 text-[10px] uppercase" data-testid="selfie-retake">
              <ArrowsCounterClockwise size={12} className="mr-1" /> Reintentar
            </Button>
            <Button onClick={apply} className="flex-1 bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase" data-testid="selfie-apply">
              <Check size={14} className="mr-1" /> Aplicar al avatar
            </Button>
          </div>
        )}

        <DialogFooter>
          {currentPhoto && (
            <Button onClick={remove} variant="outline" size="sm" className="border-red-500/30 text-red-400 text-[10px] uppercase" data-testid="selfie-remove">
              <X size={12} className="mr-1" /> Quitar foto facial
            </Button>
          )}
        </DialogFooter>
        <div className="text-[10px] text-gray-500 italic">La foto se proyecta sobre la esfera de la cabeza (offset 0.25, repeat 0.5) con `displacementMap` para volumetría real.</div>
      </DialogContent>
    </Dialog>
  );
}
